export interface TimezoneOption {
  id: string;
  iana: string;
  label: string;
  displayOffset: string;
}

export const timezones: TimezoneOption[] = [
  {
    id: "GMT",
    iana: "Etc/GMT",
    label: "GMT (UTC+00:00)",
    displayOffset: "UTC+00:00",
  },
  {
    id: "UTC",
    iana: "Etc/UTC",
    label: "UTC (UTC+00:00)",
    displayOffset: "UTC+00:00",
  },
  {
    id: "EST/EDT",
    iana: "America/New_York",
    label: "EST/EDT (UTC-05:00 / UTC-04:00)",
    displayOffset: "UTC-05:00 / UTC-04:00",
  },
  {
    id: "CST/CDT",
    iana: "America/Chicago",
    label: "CST/CDT (UTC-06:00 / UTC-05:00)",
    displayOffset: "UTC-06:00 / UTC-05:00",
  },
  {
    id: "MST/MDT",
    iana: "America/Denver",
    label: "MST/MDT (UTC-07:00 / UTC-06:00)",
    displayOffset: "UTC-07:00 / UTC-06:00",
  },
  {
    id: "PST/PDT",
    iana: "America/Los_Angeles",
    label: "PST/PDT (UTC-08:00 / UTC-07:00)",
    displayOffset: "UTC-08:00 / UTC-07:00",
  },
  {
    id: "AST/ADT",
    iana: "America/Halifax",
    label: "AST/ADT (UTC-04:00 / UTC-03:00)",
    displayOffset: "UTC-04:00 / UTC-03:00",
  },
  {
    id: "NST/NDT",
    iana: "America/St_Johns",
    label: "NST/NDT (UTC-03:30 / UTC-02:30)",
    displayOffset: "UTC-03:30 / UTC-02:30",
  },
  {
    id: "AKST/AKDT",
    iana: "America/Anchorage",
    label: "AKST/AKDT (UTC-09:00 / UTC-08:00)",
    displayOffset: "UTC-09:00 / UTC-08:00",
  },
  {
    id: "HST/HDT",
    iana: "America/Adak",
    label: "HST/HDT (UTC-10:00 / UTC-09:00)",
    displayOffset: "UTC-10:00 / UTC-09:00",
  },
  {
    id: "WET/WEST",
    iana: "Europe/Lisbon",
    label: "WET/WEST (UTC+00:00 / UTC+01:00)",
    displayOffset: "UTC+00:00 / UTC+01:00",
  },
  {
    id: "CET/CEST",
    iana: "Europe/Paris",
    label: "CET/CEST (UTC+01:00 / UTC+02:00)",
    displayOffset: "UTC+01:00 / UTC+02:00",
  },
  {
    id: "EET/EEST",
    iana: "Europe/Athens",
    label: "EET/EEST (UTC+02:00 / UTC+03:00)",
    displayOffset: "UTC+02:00 / UTC+03:00",
  },
  {
    id: "AEST/AEDT",
    iana: "Australia/Sydney",
    label: "AEST/AEDT (UTC+10:00 / UTC+11:00)",
    displayOffset: "UTC+10:00 / UTC+11:00",
  },
  {
    id: "ACST/ACDT",
    iana: "Australia/Adelaide",
    label: "ACST/ACDT (UTC+09:30 / UTC+10:30)",
    displayOffset: "UTC+09:30 / UTC+10:30",
  },
  {
    id: "NZST/NZDT",
    iana: "Pacific/Auckland",
    label: "NZST/NZDT (UTC+12:00 / UTC+13:00)",
    displayOffset: "UTC+12:00 / UTC+13:00",
  },
  {
    id: "LHST/LHDT",
    iana: "Australia/Lord_Howe",
    label: "LHST/LHDT (UTC+10:30 / UTC+11:00)",
    displayOffset: "UTC+10:30 / UTC+11:00",
  },
];

export function getTimezoneOffset(
  timeZone: string,
  date: Date = new Date(),
): number {
  try {
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone,
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
      hour12: false,
    });

    const parts = formatter.formatToParts(date);
    const getVal = (type: string) => {
      const part = parts.find((p) => p.type === type);
      return part ? parseInt(part.value, 10) : 0;
    };

    const year = getVal("year");
    const month = getVal("month");
    const day = getVal("day");
    const hour = getVal("hour") % 24;
    const minute = getVal("minute");
    const second = getVal("second");

    const tzDate = Date.UTC(year, month - 1, day, hour, minute, second);
    const utcDate = Date.UTC(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate(),
      date.getUTCHours(),
      date.getUTCMinutes(),
      date.getUTCSeconds(),
    );

    return (tzDate - utcDate) / 60000;
  } catch (e) {
    console.error("Error formatting timezone offset", e);
    return 0;
  }
}

export function parseTimeToDate(
  timeString: string | undefined | null,
  baseDate: Date | string,
): Date {
  const resultDate = new Date(baseDate);
  if (!timeString) {
    resultDate.setHours(0, 0, 0, 0);
    return resultDate;
  }

  const match = timeString.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?(?:\s+(.+))?$/i);
  if (!match) {
    resultDate.setHours(0, 0, 0, 0);
    return resultDate;
  }

  let hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  const ampm = match[3];
  const tzId = match[4] || "GMT";

  if (ampm) {
    const isPM = ampm.toUpperCase() === "PM";
    if (isPM && hours !== 12) {
      hours += 12;
    } else if (!isPM && hours === 12) {
      hours = 0;
    }
  }

  const tzConfig = timezones.find((t) => t.id === tzId);
  const iana = tzConfig ? tzConfig.iana : "Etc/GMT";

  const year = resultDate.getFullYear();
  const month = String(resultDate.getMonth() + 1).padStart(2, "0");
  const day = String(resultDate.getDate()).padStart(2, "0");
  const hh = String(hours).padStart(2, "0");
  const mm = String(minutes).padStart(2, "0");

  const tempDate = new Date(`${year}-${month}-${day}T${hh}:${mm}:00Z`);
  const offsetMinutes = getTimezoneOffset(iana, tempDate);

  const sign = offsetMinutes >= 0 ? "+" : "-";
  const absMinutes = Math.abs(offsetMinutes);
  const offsetH = String(Math.floor(absMinutes / 60)).padStart(2, "0");
  const offsetM = String(absMinutes % 60).padStart(2, "0");
  const offsetString = `${sign}${offsetH}:${offsetM}`;

  const finalIso = `${year}-${month}-${day}T${hh}:${mm}:00${offsetString}`;
  return new Date(finalIso);
}

export function formatISOToInputTime(
  isoString: string | undefined | null,
): string {
  if (!isoString) return "";

  const match = isoString.match(
    /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.\d+)?(Z|[+-]\d{2}:?\d{2})$/i,
  );
  if (!match) return "";

  const [, , hhStr, mmStr, , offsetPart] = match;
  const hours = parseInt(hhStr, 10);
  const minutes = parseInt(mmStr, 10);

  let totalOffsetMinutes = 0;
  if (offsetPart !== "Z") {
    const sign = offsetPart[0] === "+" ? 1 : -1;
    const cleanOffset = offsetPart.substring(1).replace(":", "");
    const offsetH = parseInt(cleanOffset.substring(0, 2), 10);
    const offsetM = parseInt(cleanOffset.substring(2, 4) || "0", 10);
    totalOffsetMinutes = sign * (offsetH * 60 + offsetM);
  }

  const isPM = hours >= 12;
  let displayHours = hours % 12;
  if (displayHours === 0) displayHours = 12;
  const ampm = isPM ? "PM" : "AM";

  const dateObj = new Date(isoString);
  const matchedTz = timezones.find((tz) => {
    return getTimezoneOffset(tz.iana, dateObj) === totalOffsetMinutes;
  });

  const tzId = matchedTz ? matchedTz.id : "GMT";

  return `${displayHours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")} ${ampm} ${tzId}`;
}

export const formatClockWithTz = (
  hours: number,
  minutes: number,
  light: boolean,
  tzId: string,
) => {
  return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")} ${light ? "AM" : "PM"} ${tzId}`;
};
